import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AiPageContextSync } from '@/components/ai-agent/AiPageContextSync';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Modal,
  Progress,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  CalculatorOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  FileProtectOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { SmartBreadcrumbs } from '@/components/Breadcrumbs';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import {
  DOCUMENT_ENTITY_TYPE_SUPPLIER,
  DOCUMENT_ENTITY_TYPE_SUPPLIER_ASSESSMENT,
} from '@/constants/document-entity-types';
import { documentService } from '@/services/document.service';
import {
  calculateEnterpriseScore,
  createDevelopmentPlan,
  getSupplier360,
  type DecisionCheckpoint,
  type Supplier360Assessment,
  type Supplier360Document,
  type Supplier360Workspace,
} from '@/services/supplierLifecycle.service';
import { useCurrency } from '@/contexts/CurrencyContext';

const { Title, Text } = Typography;

function segmentColor(segment?: string): string {
  switch (segment) {
    case 'Strategic':
      return 'green';
    case 'Preferred':
      return 'blue';
    case 'Approved':
      return 'cyan';
    case 'Conditional':
      return 'orange';
    case 'Restricted':
      return 'red';
    case 'Blacklisted':
      return 'red';
    default:
      return 'default';
  }
}

function resultColor(result?: string): string {
  const r = String(result || '').toLowerCase();
  if (r === 'pass') return 'success';
  if (r === 'fail') return 'error';
  if (r.includes('conditional')) return 'warning';
  return 'default';
}

function fmtDate(v?: string | Date | null): string {
  if (!v) return '—';
  const d = dayjs(v);
  return d.isValid() ? d.format('YYYY-MM-DD') : '—';
}

function str(v: unknown): string {
  if (v == null || v === '') return '—';
  return String(v);
}

export const EnterpriseDecisionCheckpoint: React.FC<{ checkpoint: DecisionCheckpoint | null }> = ({
  checkpoint,
}) => {
  if (!checkpoint) {
    return <Text type="secondary">No checkpoint data yet. Calculate Enterprise Supplier Score first.</Text>;
  }
  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Tag color={checkpoint.ready_to_expand ? 'success' : 'warning'}>
          {checkpoint.ready_to_expand ? 'Ready to expand engagement' : 'Review required before expansion'}
        </Tag>
        {checkpoint.segment ? <Tag color={segmentColor(checkpoint.segment)}>{checkpoint.segment}</Tag> : null}
        {checkpoint.composite_score != null ? <Text strong>Score: {checkpoint.composite_score}</Text> : null}
        {checkpoint.audit_due ? <Tag color="error">Audit overdue</Tag> : null}
      </Space>
      <Row gutter={[8, 8]}>
        {checkpoint.checks.map((c) => (
          <Col xs={24} sm={12} key={c.key}>
            <Card size="small" style={{ borderColor: c.pass ? '#b7eb8f' : '#ffa39e' }}>
              <Text strong>{c.label}</Text>
              <div>
                <Tag color={c.pass ? 'success' : 'error'}>{c.pass ? 'Pass' : 'Fail'}</Tag>
              </div>
              <Text type="secondary">{c.detail}</Text>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

const DocumentMiniTable: React.FC<{ rows: Supplier360Document[]; emptyText?: string }> = ({
  rows,
  emptyText = 'No documents',
}) => {
  if (!rows.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />;
  return (
    <Table
      size="small"
      rowKey="id"
      pagination={{ pageSize: 8, hideOnSinglePage: true }}
      dataSource={rows}
      columns={[
        {
          title: 'Document',
          key: 'name',
          render: (_, r) => r.document_title || r.document_name || r.file_name || '—',
        },
        { title: 'Type', dataIndex: 'document_type', width: 120, render: (v) => v || '—' },
        { title: 'Category', dataIndex: 'document_category', width: 120, render: (v) => v || '—' },
        { title: 'Uploaded', dataIndex: 'created_at', width: 120, render: (v) => fmtDate(v) },
        {
          title: '',
          key: 'actions',
          width: 100,
          render: (_, r) => (
            <Space size={4}>
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => documentService.openDocumentInNewTab(r.id).catch((e) => message.error(String(e?.message || e)))}
              />
              <Button
                type="link"
                size="small"
                icon={<DownloadOutlined />}
                onClick={async () => {
                  try {
                    const blob = await documentService.downloadDocument(r.id);
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = r.file_name || r.document_name || 'document';
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch (e: any) {
                    message.error(e?.message || 'Download failed');
                  }
                }}
              />
            </Space>
          ),
        },
      ]}
    />
  );
};

export const Supplier360Page: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const suppliersListPath = location.pathname.startsWith('/platform')
    ? '/platform/foundation/suppliers'
    : '/scm/procurement/suppliers';
  const editPath = id ? `${suppliersListPath}/${id}/edit` : suppliersListPath;
  const { formatCurrency } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<Supplier360Workspace | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [planOpen, setPlanOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<string | null>(null);
  const [form] = Form.useForm();

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    getSupplier360(id)
      .then((ws) => {
        setData(ws);
        const first = ws.quality?.assessments?.[0]?.id ?? null;
        setSelectedAssessmentId((prev) => prev || first);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const onCalculateScore = async () => {
    if (!id) return;
    try {
      await calculateEnterpriseScore(id);
      message.success('Enterprise Supplier Score updated');
      load();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Score calculation failed');
    }
  };

  const onCreatePlan = async () => {
    if (!id) return;
    const values = await form.validateFields();
    try {
      await createDevelopmentPlan(id, {
        title: values.title,
        problem_statement: values.problem_statement,
        root_cause: values.root_cause,
        target_metric: values.target_metric,
        target_value: values.target_value,
        target_completion_date: values.target_completion_date?.format('YYYY-MM-DD'),
        actions: values.action_description
          ? [{ action_description: values.action_description }]
          : undefined,
      });
      message.success('Development plan created');
      setPlanOpen(false);
      form.resetFields();
      load();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Failed to create plan');
    }
  };

  const assessments = data?.quality?.assessments ?? [];
  const selectedAssessment: Supplier360Assessment | undefined = useMemo(
    () => assessments.find((a) => a.id === selectedAssessmentId) ?? assessments[0],
    [assessments, selectedAssessmentId]
  );
  const assessmentDocsForSelected = useMemo(() => {
    const all = data?.documents?.assessments ?? data?.quality?.assessment_documents ?? [];
    if (!selectedAssessment?.id) return all;
    return all.filter((d) => d.entity_id === selectedAssessment.id);
  }, [data, selectedAssessment?.id]);
  const evidenceForSelected = useMemo(() => {
    const all = data?.quality?.assessment_evidence ?? [];
    if (!selectedAssessment?.id) return all;
    return all.filter((e) => e.assessment_id === selectedAssessment.id);
  }, [data, selectedAssessment?.id]);

  if (loading && !data) return <Spin style={{ display: 'block', margin: '48px auto' }} />;
  if (error && !data) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          showIcon
          message="Failed to load Supplier 360"
          description={error}
          action={
            <Button icon={<ReloadOutlined />} onClick={load}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }
  if (!data) return <Alert type="error" message="Supplier not found" />;

  const s = data.supplier as Record<string, unknown>;
  const score = data.intelligence.score as Record<string, unknown> | null;
  const checkpoint = data.intelligence.decision_checkpoint;
  const summary = data.summary;
  const segment = String(score?.segment ?? s.supplier_segment ?? '');

  return (
    <div style={{ margin: -24, minHeight: 'calc(100vh - 64px)' }}>
      {id ? <AiPageContextSync module="procurement" docType="Supplier" docId={id} /> : null}
      <div style={{ padding: '8px 16px 0' }}>
        <SmartBreadcrumbs />
      </div>
      <WorkspaceLayout
        fullBleed
        header={
          <EnterprisePageHeader
            documentLabel="Supplier 360°"
            documentNumber={`${str(s.supplier_code)} — ${str(s.supplier_name)}`}
            statusTag={
              <Space wrap size={4}>
                <Tag color={s.is_active ? 'success' : 'default'}>{s.is_active ? 'Active' : 'Inactive'}</Tag>
                <Tag color={s.is_approved ? 'blue' : 'orange'}>
                  {s.is_approved ? 'Approved' : 'Pending approval'}
                </Tag>
                <Tag>{str(s.qualification_status ?? 'Pending')}</Tag>
                {segment ? <Tag color={segmentColor(segment)}>{segment}</Tag> : null}
                {summary?.blacklist_active ? <Tag color="red">Blacklisted</Tag> : null}
              </Space>
            }
            subtitle={
              <Text type="secondary">
                {[s.city, s.country, s.contact_person, s.email, s.phone]
                  .filter(Boolean)
                  .map(String)
                  .join(' · ') || 'Supplier workspace'}
              </Text>
            }
            facts={
              <Space wrap size="large">
                {(score?.composite_score != null || s.enterprise_supplier_score != null) && (
                  <Text>
                    Enterprise score:{' '}
                    <Text strong>{String(score?.composite_score ?? s.enterprise_supplier_score)}</Text>
                  </Text>
                )}
                {summary?.latest_assessment && (
                  <Text>
                    Latest assessment:{' '}
                    <Text strong>{String(summary.latest_assessment.assessment_number)}</Text>{' '}
                    <Tag color={resultColor(String(summary.latest_assessment.pass_fail_result))}>
                      {String(summary.latest_assessment.pass_fail_result || summary.latest_assessment.status)}
                    </Tag>
                  </Text>
                )}
                <Text type="secondary">
                  {[s.address_line1, s.city, s.country].filter(Boolean).map(String).join(', ')}
                </Text>
              </Space>
            }
            primaryActions={
              <Space wrap>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(suppliersListPath)}>
                  Suppliers
                </Button>
                <Button icon={<EditOutlined />} onClick={() => navigate(editPath)}>
                  Edit
                </Button>
                <Button type="primary" icon={<CalculatorOutlined />} onClick={onCalculateScore}>
                  Calculate score
                </Button>
                <Button icon={<PlusOutlined />} onClick={() => setPlanOpen(true)}>
                  Development plan
                </Button>
                <Button
                  icon={<FileProtectOutlined />}
                  onClick={() => {
                    setActiveTab('assessments');
                  }}
                >
                  Assessments
                </Button>
                <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
                  Refresh
                </Button>
              </Space>
            }
          />
        }
      >
        <div style={{ padding: '0 16px 24px' }}>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={8} md={4}>
            <Card size="small">
              <Statistic title="Open POs" value={summary?.open_po_count ?? 0} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small">
              <Statistic title="Open PO value" value={formatCurrency(summary?.open_po_value ?? 0)} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small">
              <Statistic title="GRNs" value={summary?.grn_count ?? 0} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small">
              <Statistic title="Open AP" value={formatCurrency(summary?.open_ap_balance ?? 0)} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small">
              <Statistic title="Assessments" value={summary?.assessment_count ?? 0} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small">
              <Statistic
                title="Documents"
                value={(summary?.supplier_document_count ?? 0) + (summary?.assessment_document_count ?? 0)}
              />
            </Card>
          </Col>
        </Row>

        {score?.delivery_score != null ? (
          <Card size="small" title="7-dimension scores" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              {['delivery', 'quality', 'commercial', 'financial', 'technical', 'compliance', 'collaboration'].map(
                (dim) => (
                  <Col xs={12} sm={8} md={6} lg={3} key={dim} style={{ marginBottom: 8 }}>
                    <Text type="secondary" style={{ textTransform: 'capitalize' }}>
                      {dim}
                    </Text>
                    <Progress percent={Number(score[`${dim}_score`] ?? 0)} size="small" />
                  </Col>
                )
              )}
            </Row>
          </Card>
        ) : null}

        <Card title="Enterprise Decision Checkpoint" style={{ marginBottom: 16 }} size="small">
          <EnterpriseDecisionCheckpoint checkpoint={checkpoint} />
        </Card>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          destroyInactiveTabPane={false}
          items={[
            {
              key: 'overview',
              label: 'Overview',
              children: (
                <Card size="small">
                  <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3 }}>
                    <Descriptions.Item label="Code">{str(s.supplier_code)}</Descriptions.Item>
                    <Descriptions.Item label="Name">{str(s.supplier_name)}</Descriptions.Item>
                    <Descriptions.Item label="Category">{str(s.supplier_category ?? s.category)}</Descriptions.Item>
                    <Descriptions.Item label="Type">{str(s.supplier_type)}</Descriptions.Item>
                    <Descriptions.Item label="Contact">{str(s.contact_person)}</Descriptions.Item>
                    <Descriptions.Item label="Email">{str(s.email)}</Descriptions.Item>
                    <Descriptions.Item label="Phone">{str(s.phone)}</Descriptions.Item>
                    <Descriptions.Item label="Mobile">{str(s.mobile)}</Descriptions.Item>
                    <Descriptions.Item label="City">{str(s.city)}</Descriptions.Item>
                    <Descriptions.Item label="Country">{str(s.country)}</Descriptions.Item>
                    <Descriptions.Item label="Payment terms">{str(s.payment_terms)}</Descriptions.Item>
                    <Descriptions.Item label="Lead time">
                      {s.lead_time_days != null ? `${s.lead_time_days} days` : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Tax / TRN">
                      {str(s.tax_registration_number ?? s.vat_number ?? s.tax_number)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Qualification">
                      {str(s.qualification_status)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Segment">{segment || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Address" span={3}>
                      {[s.address_line1, s.address_line2, s.city, s.country].filter(Boolean).map(String).join(', ') ||
                        '—'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              ),
            },
            {
              key: 'commercial',
              label: `Commercial (${data.commercial.purchase_orders.length})`,
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Card size="small" title="Purchase orders">
                    <Table
                      size="small"
                      rowKey="id"
                      dataSource={data.commercial.purchase_orders as object[]}
                      columns={[
                        {
                          title: 'PO',
                          dataIndex: 'po_number',
                          render: (v: string, r: { id?: string }) =>
                            r.id ? <Link to={`/scm/procurement/purchase-orders/${r.id}`}>{v}</Link> : v,
                        },
                        { title: 'Date', dataIndex: 'po_date', render: (v) => fmtDate(v) },
                        { title: 'Delivery', dataIndex: 'delivery_date', render: (v) => fmtDate(v) },
                        { title: 'Status', dataIndex: 'po_status', render: (v) => <Tag>{v}</Tag> },
                        {
                          title: 'Value',
                          dataIndex: 'grand_total',
                          render: (v: number) => formatCurrency(v),
                        },
                      ]}
                      pagination={{ pageSize: 10 }}
                    />
                  </Card>
                  <Card size="small" title="Inquiries">
                    <Table
                      size="small"
                      rowKey="id"
                      dataSource={data.commercial.inquiries as object[]}
                      columns={[
                        { title: 'Inquiry', dataIndex: 'inquiry_number' },
                        { title: 'Date', dataIndex: 'inquiry_date', render: (v) => fmtDate(v) },
                        { title: 'Status', dataIndex: 'status' },
                      ]}
                      pagination={{ pageSize: 8 }}
                    />
                  </Card>
                </Space>
              ),
            },
            {
              key: 'operational',
              label: `Operational (${data.operational.grns.length})`,
              children: (
                <Table
                  size="small"
                  rowKey="id"
                  dataSource={data.operational.grns as object[]}
                  columns={[
                    { title: 'GRN', dataIndex: 'grn_number' },
                    { title: 'Date', dataIndex: 'grn_date', render: (v) => fmtDate(v) },
                    { title: 'Status', dataIndex: 'grn_status', render: (v) => <Tag>{v}</Tag> },
                  ]}
                  pagination={{ pageSize: 10 }}
                />
              ),
            },
            {
              key: 'financial',
              label: `Financial (${data.financial.invoices.length})`,
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Card size="small" title="Supplier invoices">
                    <Table
                      size="small"
                      rowKey="id"
                      dataSource={data.financial.invoices as object[]}
                      columns={[
                        { title: 'Invoice', dataIndex: 'invoice_number' },
                        {
                          title: 'Date',
                          dataIndex: 'supplier_invoice_date',
                          render: (v) => fmtDate(v),
                        },
                        { title: 'Status', dataIndex: 'invoice_status' },
                        { title: 'Payment', dataIndex: 'payment_status' },
                        {
                          title: 'Amount',
                          dataIndex: 'grand_total',
                          render: (v: number) => formatCurrency(v),
                        },
                        {
                          title: 'Balance',
                          dataIndex: 'balance_due',
                          render: (v: number) => formatCurrency(v ?? 0),
                        },
                      ]}
                      pagination={{ pageSize: 10 }}
                    />
                  </Card>
                  <Card size="small" title="Advance requests">
                    <Table
                      size="small"
                      rowKey="id"
                      dataSource={data.financial.advances as object[]}
                      columns={[
                        { title: 'Request', dataIndex: 'advance_request_number' },
                        { title: 'Date', dataIndex: 'requested_date', render: (v) => fmtDate(v) },
                        { title: 'Status', dataIndex: 'status' },
                        {
                          title: 'Amount',
                          dataIndex: 'requested_amount',
                          render: (v: number) => formatCurrency(v),
                        },
                      ]}
                      pagination={{ pageSize: 8 }}
                    />
                  </Card>
                </Space>
              ),
            },
            {
              key: 'assessments',
              label: (
                <span>
                  <FileProtectOutlined /> Assessments ({assessments.length})
                </span>
              ),
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Alert
                    type="info"
                    showIcon
                    message="Supplier assessment history, scores, attached documents, and question evidence"
                    action={
                      <Button size="small" onClick={() => navigate('/scm/quality/supplier-assessments')}>
                        Open assessments module
                      </Button>
                    }
                  />
                  <Row gutter={16}>
                    <Col xs={24} lg={10}>
                      <Card size="small" title="Assessment list">
                        <Table
                          size="small"
                          rowKey="id"
                          pagination={{ pageSize: 8 }}
                          dataSource={assessments}
                          rowClassName={(r) => (r.id === selectedAssessment?.id ? 'ant-table-row-selected' : '')}
                          onRow={(r) => ({
                            onClick: () => setSelectedAssessmentId(r.id),
                            style: { cursor: 'pointer' },
                          })}
                          columns={[
                            {
                              title: '#',
                              dataIndex: 'assessment_number',
                              width: 120,
                              render: (v: string, r: { id: string }) => (
                                <Button
                                  type="link"
                                  size="small"
                                  style={{ padding: 0 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/scm/quality/supplier-assessments/${r.id}`);
                                  }}
                                >
                                  {v}
                                </Button>
                              ),
                            },
                            { title: 'Date', dataIndex: 'assessment_date', width: 110, render: (v) => fmtDate(v) },
                            {
                              title: 'Result',
                              dataIndex: 'pass_fail_result',
                              width: 110,
                              render: (v) => <Tag color={resultColor(v)}>{v || '—'}</Tag>,
                            },
                            {
                              title: 'Score',
                              dataIndex: 'percentage_score',
                              width: 80,
                              render: (v) => (v != null ? `${Number(v).toFixed(0)}%` : '—'),
                            },
                          ]}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} lg={14}>
                      {selectedAssessment ? (
                        <Card
                          size="small"
                          title={`${selectedAssessment.assessment_number || 'Assessment'} details`}
                          extra={
                            <Space>
                              <Button
                                size="small"
                                type="link"
                                onClick={() =>
                                  navigate(`/scm/quality/supplier-assessments/${selectedAssessment.id}`)
                                }
                              >
                                Open full page
                              </Button>
                              <Tag color={resultColor(selectedAssessment.pass_fail_result)}>
                                {selectedAssessment.pass_fail_result || selectedAssessment.status || '—'}
                              </Tag>
                            </Space>
                          }
                        >
                          <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
                            <Descriptions.Item label="Type">
                              {str(selectedAssessment.assessment_type)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Status">{str(selectedAssessment.status)}</Descriptions.Item>
                            <Descriptions.Item label="Template">
                              {str(selectedAssessment.template_name)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Category">
                              {str(selectedAssessment.template_category)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Date">
                              {fmtDate(selectedAssessment.assessment_date)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Next due">
                              {fmtDate(selectedAssessment.next_assessment_date)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Score">
                              {selectedAssessment.percentage_score != null
                                ? `${Number(selectedAssessment.percentage_score).toFixed(1)}%`
                                : '—'}
                              {selectedAssessment.total_score != null
                                ? ` (${selectedAssessment.total_score}/${selectedAssessment.max_possible_score ?? '—'})`
                                : ''}
                            </Descriptions.Item>
                            <Descriptions.Item label="Passing">
                              {selectedAssessment.template_passing_score != null
                                ? `${selectedAssessment.template_passing_score}%`
                                : '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Recommendation" span={2}>
                              {str(selectedAssessment.recommendation)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Comments" span={2}>
                              {str(selectedAssessment.evaluator_comments)}
                            </Descriptions.Item>
                          </Descriptions>

                          {selectedAssessment.percentage_score != null ? (
                            <Progress
                              percent={Number(selectedAssessment.percentage_score)}
                              status={
                                String(selectedAssessment.pass_fail_result || '').toLowerCase() === 'fail'
                                  ? 'exception'
                                  : 'active'
                              }
                              style={{ marginBottom: 16 }}
                            />
                          ) : null}

                          <Title level={5}>Assessment documents</Title>
                          <DocumentMiniTable
                            rows={assessmentDocsForSelected}
                            emptyText="No documents linked to this assessment"
                          />

                          <Title level={5} style={{ marginTop: 16 }}>
                            Evidence from responses
                          </Title>
                          {evidenceForSelected.length ? (
                            <Table
                              size="small"
                              rowKey="id"
                              pagination={false}
                              dataSource={evidenceForSelected}
                              columns={[
                                { title: 'Section', dataIndex: 'section', width: 120 },
                                { title: 'Question', dataIndex: 'question_text', ellipsis: true },
                                { title: 'Type', dataIndex: 'evidence_type', width: 100 },
                                {
                                  title: 'Link',
                                  dataIndex: 'evidence_url',
                                  width: 90,
                                  render: (url: string) =>
                                    url ? (
                                      <a href={url} target="_blank" rel="noreferrer">
                                        Open
                                      </a>
                                    ) : (
                                      '—'
                                    ),
                                },
                              ]}
                            />
                          ) : (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No evidence URLs recorded" />
                          )}

                          <div style={{ marginTop: 16 }}>
                            <EntityAttachmentsCard
                              entityType={DOCUMENT_ENTITY_TYPE_SUPPLIER_ASSESSMENT}
                              entityId={selectedAssessment.id}
                              title="Upload / manage assessment files"
                              onDocumentsChanged={load}
                            />
                          </div>
                        </Card>
                      ) : (
                        <Empty description="No assessments for this supplier yet" />
                      )}
                    </Col>
                  </Row>
                </Space>
              ),
            },
            {
              key: 'documents',
              label: `Documents (${(summary?.supplier_document_count ?? 0) + (summary?.assessment_document_count ?? 0)})`,
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Card size="small" title="Supplier master documents">
                    <DocumentMiniTable
                      rows={data.documents?.supplier ?? []}
                      emptyText="No supplier-level documents"
                    />
                    {id ? (
                      <div style={{ marginTop: 12 }}>
                        <EntityAttachmentsCard
                          entityType={DOCUMENT_ENTITY_TYPE_SUPPLIER}
                          entityId={id}
                          title="Upload / manage supplier files"
                          onDocumentsChanged={load}
                        />
                      </div>
                    ) : null}
                  </Card>
                  <Card size="small" title="All assessment documents">
                    <DocumentMiniTable
                      rows={data.documents?.assessments ?? []}
                      emptyText="No assessment documents"
                    />
                  </Card>
                </Space>
              ),
            },
            {
              key: 'quality',
              label: 'Quality / Blacklist',
              children: (
                <Card size="small" title="Active blacklist records">
                  <Table
                    size="small"
                    rowKey="id"
                    dataSource={data.quality.blacklist as object[]}
                    columns={[
                      { title: 'Reason', dataIndex: 'reason', ellipsis: true },
                      { title: 'Status', dataIndex: 'status' },
                      { title: 'Effective', dataIndex: 'effective_from', render: (v) => fmtDate(v) },
                    ]}
                    locale={{ emptyText: 'Not blacklisted' }}
                    pagination={false}
                  />
                </Card>
              ),
            },
            {
              key: 'development',
              label: 'Development',
              children: (
                <Table
                  size="small"
                  rowKey="id"
                  dataSource={data.intelligence.development_plans as object[]}
                  columns={[
                    { title: 'Title', dataIndex: 'title' },
                    { title: 'Problem', dataIndex: 'problem_statement', ellipsis: true },
                    { title: 'Status', dataIndex: 'status' },
                    { title: 'Target', dataIndex: 'target_metric' },
                  ]}
                  pagination={{ pageSize: 10 }}
                />
              ),
            },
          ]}
        />
        </div>
      </WorkspaceLayout>

      <Modal
        title="Supplier Development Plan"
        open={planOpen}
        onCancel={() => setPlanOpen(false)}
        onOk={onCreatePlan}
        width={560}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="problem_statement" label="Problem" rules={[{ required: true }]}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="root_cause" label="Root cause">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="target_metric" label="Target metric">
            <Input placeholder="e.g. On-Time Delivery %" />
          </Form.Item>
          <Form.Item name="target_value" label="Target value">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="target_completion_date" label="Target completion">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="action_description" label="Corrective action">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Supplier360Page;
